import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from '@tanstack/react-router'
import { createClient } from '@connectrpc/connect'
import { createConnectTransport } from '@connectrpc/connect-web'
import { SecuritiesReportChunkService } from '../../../../proto/_gen/_gen.securitiesReportChunk.service_connect'
import { GetSecuritiesReportChunkByIdRequest, GetSecuritiesReportChunksByDocIdRequest } from '../../../../proto/_gen/_gen.securitiesReportChunk.service_pb'
import { authenticator } from '../../_shared/authService'
import * as DomainModel from 'poc-ai-biz-irdb_domain-model'
import { convertSecuritiesReportChunkToDomainModel } from '../../../../proto/_gen.converters'
import { SecuritiesReportChunkDetailPageProps } from '../model'
import { Loadable } from '../../../../_shared/lib'

declare var IRDB_BACKEND_API_URL: string | undefined

export const useSecuritiesReportChunkDetailPageDatafetch = (): SecuritiesReportChunkDetailPageProps => {
  const params = useParams({ from: '/_auth_required/securities-report-chunks/$securitiesReportChunkId' })
  const navigate = useNavigate()

  // URLパラメータから securitiesReportChunkId を取得
  const securitiesReportChunkId = params.securitiesReportChunkId

  // 状態管理
  const [currentSecuritiesReportChunk, setCurrentSecuritiesReportChunk] = useState<SecuritiesReportChunkDetailPageProps['currentSecuritiesReportChunk']>({
    isLoading: true,
  })

  const [allSecuritiesReportChunks, setAllSecuritiesReportChunks] = useState<SecuritiesReportChunkDetailPageProps['allSecuritiesReportChunks']>({
    isLoading: true,
  })

  // API クライアント
  const backendApiUrl = IRDB_BACKEND_API_URL
  if (!backendApiUrl) {
    throw new Error('The IRDB_BACKEND_API_URL environment variable is not set')
  }

  const transport = createConnectTransport({
    baseUrl: backendApiUrl,
    interceptors: [authenticator],
  })
  const client = createClient(SecuritiesReportChunkService, transport)

  // 指定されたSecuritiesReportChunkを取得
  useEffect(() => {
    if (!securitiesReportChunkId) {
      setCurrentSecuritiesReportChunk({
        isLoading: false,
        error: 'securitiesReportChunkId is required',
      })
      setAllSecuritiesReportChunks({
        isLoading: false,
        error: 'securitiesReportChunkId is required',
      })
      return
    }

    const fetchCurrentSecuritiesReportChunk = async () => {
      try {
        setCurrentSecuritiesReportChunk({ isLoading: true })

        // securitiesReportChunkIdで特定のSecuritiesReportChunkを取得
        const request = new GetSecuritiesReportChunkByIdRequest({
          id: securitiesReportChunkId,
        })

        const response = await client.getSecuritiesReportChunkById(request)

        if (response.error) {
          throw new Error(response.error.message || 'Failed to fetch SecuritiesReportChunk')
        }

        if (!response.securitiesReportChunk) {
          throw new Error('SecuritiesReportChunk not found')
        }

        // ドメインモデルに変換
        const currentChunk = convertSecuritiesReportChunkToDomainModel(response.securitiesReportChunk)

        setCurrentSecuritiesReportChunk({
          isLoading: false,
          value: {
            ...currentChunk,
            isLeaf: true, // 単一チャンクの場合はleafとして扱う
          },
        })

        // 同じdocIdの全チャンクも取得
        const allChunksRequest = new GetSecuritiesReportChunksByDocIdRequest({
          docId: currentChunk.docId,
          match: 'exact',
        })

        const allChunksResponse = await client.getSecuritiesReportChunksByDocId(allChunksRequest)

        if (allChunksResponse.error) {
          console.warn('Failed to fetch all chunks:', allChunksResponse.error.message)
          setAllSecuritiesReportChunks({
            isLoading: false,
            error: allChunksResponse.error.message || 'Failed to fetch all chunks',
          })
          return
        }

        // 全てのチャンクをドメインモデルに変換してisLeafを計算
        const allDomainChunks = allChunksResponse.securitiesReportChunks.map(convertSecuritiesReportChunkToDomainModel)

        const sortedChunks = [...allDomainChunks].sort((a, b) => {
          const aIndex = a.chunkIndex ?? 0
          const bIndex = b.chunkIndex ?? 0
          return aIndex - bIndex
        })

        const chunksWithIsLeaf = sortedChunks.map((chunk, index) => {
          const currentLayer = chunk.titlesStack.length
          let isLeaf = true

          if (index + 1 < sortedChunks.length) {
            const nextChunk = sortedChunks[index + 1]
            if (nextChunk) {
              const nextLayer = nextChunk.titlesStack.length
              isLeaf = nextLayer <= currentLayer
            }
          }

          return {
            ...chunk,
            isLeaf,
          }
        })

        setAllSecuritiesReportChunks({
          isLoading: false,
          value: chunksWithIsLeaf,
        })
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        setCurrentSecuritiesReportChunk({
          isLoading: false,
          error: errorMessage,
        })
        setAllSecuritiesReportChunks({
          isLoading: false,
          error: errorMessage,
        })
      }
    }

    fetchCurrentSecuritiesReportChunk()
  }, [securitiesReportChunkId])

  // イベントハンドラー
  const handleSecuritiesReportChunkClick = (securitiesReportChunk: DomainModel.SecuritiesReportChunk) => {
    // securitiesReportChunkIdに基づいて直接ナビゲート
    navigate({
      to: '/securities-report-chunks/$securitiesReportChunkId',
      params: { securitiesReportChunkId: securitiesReportChunk.id },
    })
  }

  const handleBreadcrumbClick = (titleIndex: number) => {
    // 現在のチャンクから適切なチャンクを探す
    if (!currentSecuritiesReportChunk.value || !allSecuritiesReportChunks.value) return

    const currentTitlesStack = currentSecuritiesReportChunk.value.titlesStack
    const targetTitlesStack = currentTitlesStack.slice(0, titleIndex + 1)

    // 対象のtitlesStackと一致するチャンクを探す
    const targetChunk = allSecuritiesReportChunks.value.find(
      (chunk) => chunk.titlesStack.length === targetTitlesStack.length && chunk.titlesStack.every((title, index) => title === targetTitlesStack[index]),
    )

    if (targetChunk) {
      navigate({
        to: '/securities-report-chunks/$securitiesReportChunkId',
        params: { securitiesReportChunkId: targetChunk.id },
      })
    }
  }

  return {
    currentSecuritiesReportChunk,
    allSecuritiesReportChunks,
    onSecuritiesReportChunkClick: handleSecuritiesReportChunkClick,
    onBreadcrumbClick: handleBreadcrumbClick,
  }
}
